import React from 'react';

/**
 * Styled confirmation dialog — replaces window.confirm.
 * Usage:
 *   const [confirm, setConfirm] = useState(null);
 *   setConfirm({ title, message, onConfirm: async () => {...} });
 *   <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
 */
const ConfirmDialog = ({ state, onClose }) => {
  const [busy, setBusy] = React.useState(false);
  if (!state) return null;
  const {
    title = 'Wirklich fortfahren?',
    message = '',
    confirmLabel = 'Bestätigen',
    cancelLabel = 'Abbrechen',
    danger = false,
    onConfirm,
  } = state;

  const handleConfirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm?.();
    } finally {
      setBusy(false);
      onClose?.();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 vp-anim-fade"
      onClick={() => { if (!busy) onClose?.(); }}
      data-testid="confirm-dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 ring-1 ring-black/5 vp-anim-scale"
      >
        <div className="flex items-start gap-4">
          <div
            className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
              danger ? 'bg-red-100 text-red-600' : 'bg-accent/15 text-accent'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d={
                  danger
                    ? 'M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z'
                    : 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                }
              />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-semibold text-lg text-[#0A0A0A]">{title}</h3>
            {message && <p className="text-sm text-muted-foreground mt-2 whitespace-pre-line">{message}</p>}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 rounded-md text-sm font-medium bg-secondary hover:bg-gray-200 transition disabled:opacity-50"
            data-testid="confirm-cancel"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy}
            className={`px-4 py-2 rounded-md text-sm font-medium text-white transition disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2 ${
              danger ? 'bg-red-600 hover:bg-red-700' : 'bg-primary hover:bg-primary/90'
            }`}
            data-testid="confirm-ok"
          >
            {busy && (
              <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round">
                <path d="M12 3a9 9 0 1 1-9 9" />
              </svg>
            )}
            {busy ? 'Wird ausgeführt …' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
